import React from 'react';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, TypeCabinCategory } from '../../interfaces/ICabin';
import { isEmpty } from '../../helpers/validation';
import { updateRequestFormFields } from '../../actions/requestFormActions';
import { TextCollapsible } from '../TextCollapsible/TextCollapsible';
import { FullscreenImageRenderer } from '../FullscreenImageRenderer/FullscreenImageRenderer';
import { LocationIcon } from '../Icons/LocationIcon';
import { AxisIcon } from '../Icons/AxisIcon';
import { UserIcon } from '../Icons/UserIcon';
import { BedIcon } from '../Icons/BedIcon';
import { CheckMarkIcon } from '../Icons/CheckMarkIcon';
import { Size } from '../Icons/Size';

import { RequestFormSelect } from './RequestFormSelect';
import { getCurrencyString } from '../../components/PriceString';
import { IRate } from '../../interfaces';
import { GuaranteeCabinLabel } from '../GuaranteeCabinLabel';
import { config } from '../../config';
import styled from '@emotion/styled';
import { CabinTariffDialog } from '../CabinTariffDialog/CabinTariffDialog';
import { createPortal } from 'react-dom';

interface IRequestFormCabinType {
    selectedCabinId: number;
    cabinsObj: any;
    onChange: typeof updateRequestFormFields;
    selectedRates?: IRate[];
    isAllInclusiveSelected?: boolean;
    passengersNum: number;
    tariffsDescription: string;
}

interface IRequestFormAdvantages {
    isOpen: boolean;
    advantages?: string[];
    amenities?: string[];
    toggleStateOpen: (isOpen: boolean) => void;
}

interface ICabineTypeSelect {
    selectedCabinId: number;
    selectedCabinKind: TypeCabinCategory;
    onChange: typeof updateRequestFormFields;
    cabins: Record<string, ICabinInfo[]>;
    selectedRates?: IRate[];
    isAllInclusive?: boolean;
    passengersNum: number;
}

const StyledGuaranteeLabel = styled(GuaranteeCabinLabel)`
    margin-top: 16px;
`;

const TariffInfo = styled.span`
    font-size: 15px;
    margin-bottom: 12px;
    color: ${({ theme }) => theme.text.color.primary};
    line-height: 24px;
    display: flex;
    gap: 12px;
    align-items: center;
    font-weight: 600;
`;

const TariffLink = styled.button`
    color: ${({ theme }) => theme.link.primary};
    font-size: 14px;
    line-height: 18px;
    border: none;
    margin: 0;
    padding: 0;
    text-decoration: underline;
    line-height: 22px;
    background: none;
`;
const CabineTypeSelect = (props: ICabineTypeSelect) => {
    const {
        selectedCabinId,
        selectedCabinKind,
        onChange,
        cabins,
        selectedRates,
        isAllInclusive,
        passengersNum,
    } = props;
    const cabinsForSelectedKind: ICabinInfo[] = cabins[selectedCabinKind];

    const cabinsAllInclusive = cabinsForSelectedKind?.map((cabin) => {
        const rate = selectedRates.find((rate) => rate.cabintype_id === cabin.cabinType.nid);
        const cabinType = cabin.cabinType;
        const catalogPerPersonPrice = cabin.catalogPerPersonPrice;
        const currency = cabin.currency;
        const perPersonPrice =
            rate?.all_in_price?.per_person_price !== null
                ? rate?.all_in_price?.per_person_price
                : 0;
        const discountPercentage = cabin.discountPercentage;
        const isValidPrice = cabin.perPersonPrice && perPersonPrice > 0;
        const currencyString = isValidPrice
            ? `(${getCurrencyString(rate?.all_in_price?.per_person_price, cabin.currency)} ${__(
                  'p.P.',
                  'dreamlines',
              )})`
            : `(${__('sold out', 'dreamlines')})`;

        const passengersNumString = isValidPrice
            ? ` ${__('for {{ n }} persons', 'dreamlines').replace('{{ n }}', passengersNum)}`
            : null;

        return {
            rate,
            cabinType,
            catalogPerPersonPrice,
            currency,
            perPersonPrice,
            discountPercentage,
            currencyString,
            passengersNumString,
        };
    });

    const soldOutIconString = '\u00A0 × ';
    return (
        <RequestFormSelect
            defaultValue={selectedCabinId}
            id="cabine-type-select"
            onChange={(nid) => onChange({ cabinNid: Number(nid) })}
            isAllInclusive={isAllInclusive}
        >
            {cabinsForSelectedKind && cabinsForSelectedKind.length > 0 && !isAllInclusive && (
                <optgroup key={selectedCabinKind} label={__(selectedCabinKind, 'dreamlines')}>
                    {cabinsForSelectedKind.map((cabin, i) => (
                        <option
                            disabled={!cabin?.perPersonPrice}
                            key={`${selectedCabinKind}-${i}`}
                            value={cabin?.cabinType.nid}
                        >
                            {!cabin?.perPersonPrice && soldOutIconString}
                            {cabin?.cabinType.labels.labelForRequestForm}
                        </option>
                    ))}
                </optgroup>
            )}

            {cabinsAllInclusive?.length > 0 && isAllInclusive && (
                <optgroup key={selectedCabinKind} label={__(selectedCabinKind, 'dreamlines')}>
                    {cabinsAllInclusive.map((cabin, i) => (
                        <option
                            disabled={!cabin?.perPersonPrice}
                            key={`${selectedCabinKind}-${i}`}
                            value={cabin?.cabinType.nid}
                        >
                            {!cabin?.perPersonPrice && soldOutIconString}
                            {cabin?.cabinType.title}
                            {cabin?.currencyString}
                            {cabin?.passengersNumString}
                            {` ${__('ALL-IN', 'dreamlines')}`}
                        </option>
                    ))}
                </optgroup>
            )}
        </RequestFormSelect>
    );
};

const RequestFormAdvantages = (props: IRequestFormAdvantages) => {
    const { isOpen, toggleStateOpen, amenities, advantages } = props;
    const nonEmptyAmenities = amenities?.filter(Boolean);
    const nonEmptyAdvantages = advantages?.filter(Boolean);
    const hasAnyAmenities = nonEmptyAmenities?.length > 0;
    const hasAnyAdvantages = nonEmptyAdvantages?.length > 0;

    if (!hasAnyAmenities && !hasAnyAdvantages) {
        return null;
    }

    return (
        <div className="selectedCabinOverview__advantagesWrapper">
            <div
                className={`selectedCabinOverview__advantages ${
                    isOpen ? 'selectedCabinOverview__advantages--is-open' : ''
                }`}
                style={{ height: isOpen ? 'auto' : '100px' }}
            >
                {hasAnyAmenities && (
                    <>
                        <span>
                            <b>{__('cruiseData.amenities', 'dreamlines')}: </b>
                        </span>
                        <br />
                        <ul>
                            {nonEmptyAmenities.map((amenitie, i) => (
                                <li key={`amenitie${i}`}>
                                    <CheckMarkIcon width="13" height="13" />
                                    {amenitie}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {hasAnyAdvantages && (
                    <>
                        <span>
                            <b>{__('cruiseData.advantages', 'dreamlines')}:</b>
                        </span>
                        <br />
                        <ul>
                            {nonEmptyAdvantages.map((advantage, i) => (
                                <li key={`advantage${i}`}>
                                    <CheckMarkIcon width="13" height="13" />
                                    {advantage}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
            <TextCollapsible isOpen={isOpen} toogleStateOpen={toggleStateOpen} />
        </div>
    );
};

const RequestFormCabinType = (props: IRequestFormCabinType) => {
    const {
        selectedCabinId,
        cabinsObj = {},
        onChange,
        selectedRates,
        isAllInclusiveSelected,
        passengersNum,
        tariffsDescription,
    } = props;

    const [isAdvantagesOpen, toggleAdvantagesState] = React.useState(false);
    const [showFullCabinImage, toggleFullCabinImage] = React.useState(false);
    const [showTariffDialog, toggleTariffDialog] = React.useState(false);

    const toggleStateOpen = (isOpen) => {
        toggleAdvantagesState(isOpen);
    };

    const toggleImageRenderer = () => {
        toggleFullCabinImage(!showFullCabinImage);
    };

    let selectedCabinObj: ICabinInfo = null;
    let selectedCabinKind: TypeCabinCategory = null;

    const cabins: Record<string, ICabinInfo[]> = {};

    for (const cabin of Object.keys(cabinsObj)) {
        if (cabin !== 'withoutPrice') {
            cabins[cabin] = [];
        }
        if (cabinsObj[cabin].cabins.length > 0) {
            cabinsObj[cabin].cabins.forEach((c, i) => {
                if (c.cabinType.nid === selectedCabinId) {
                    selectedCabinKind = c.cabinType.kind;
                    selectedCabinObj = c;
                }
                cabins[c.cabinType.kind].push(c);
            });
        }
    }

    // for it will be disabled, because there are few problems with large image urls
    // they are incorrect
    const isImageExpandAvailable = false; // Boolean(selectedCabinObj?.cabinType.image.big);

    return (
        <div className="requestFormContainer__cabineType">
            <TariffInfo>
                {__(`Cabin type`, 'dreamlines')}{' '}
                {tariffsDescription && (
                    <>
                        {__('cabinTariff', 'dreamlines')}
                        <TariffLink onClick={() => toggleTariffDialog(true)}>
                            {__('tariffDescription', 'dreamlines')}
                        </TariffLink>
                    </>
                )}
            </TariffInfo>

            <div className="requestFormContainer__cabineType__select">
                <CabineTypeSelect
                    selectedCabinId={selectedCabinId}
                    selectedCabinKind={selectedCabinKind}
                    cabins={cabins}
                    onChange={onChange}
                    selectedRates={selectedRates}
                    isAllInclusive={isAllInclusiveSelected}
                    passengersNum={passengersNum}
                />
            </div>
            {selectedCabinObj?.cabinType?.isGuaranteeCabin && <StyledGuaranteeLabel />}
            <div className="selectedCabinOverview">
                <div
                    className="selectedCabinOverview__imgWrapper"
                    style={{
                        backgroundImage: `url(${
                            selectedCabinObj?.cabinType.image.original ||
                            `${config.images}/common/placeholder.png`
                        })`,
                    }}
                >
                    {isImageExpandAvailable && (
                        <button
                            className="selectedCabinOverview__enlargeBtn"
                            onClick={toggleImageRenderer}
                        >
                            <Size width={20} height={20} style={{ fill: '#fff' }} />
                        </button>
                    )}
                </div>
                <div className="selectedCabinOverview__info">
                    <ul className="selectedCabinOverview__description">
                        {!isEmpty(selectedCabinObj?.cabinType.maxPassengers) && (
                            <li>
                                <UserIcon width={19} height={19} />
                                <span>
                                    {__('cruiseData.occupancy', 'dreamlines')}{' '}
                                    {selectedCabinObj?.cabinType.maxPassengers}
                                </span>
                            </li>
                        )}
                        {!isEmpty(selectedCabinObj?.cabinType.size) && (
                            <li>
                                <AxisIcon width={19} height={19} />
                                <span>
                                    {__('cruiseData.cabinSize', 'dreamlines')}{' '}
                                    {selectedCabinObj?.cabinType.size}
                                </span>
                            </li>
                        )}
                        {!isEmpty(selectedCabinObj?.cabinType.bed) && (
                            <li>
                                <BedIcon width={19} height={19} />
                                {selectedCabinObj?.cabinType.bed}
                            </li>
                        )}
                        {!isEmpty(selectedCabinObj?.cabinType.location) && (
                            <li>
                                <LocationIcon width={19} height={19} />
                                {selectedCabinObj?.cabinType.location}
                            </li>
                        )}
                    </ul>

                    {showFullCabinImage && isImageExpandAvailable && (
                        <FullscreenImageRenderer
                            imageAlt={selectedCabinObj?.cabinType.title}
                            imageSrc={selectedCabinObj?.cabinType.image.big}
                            onClose={toggleImageRenderer}
                        />
                    )}
                    <RequestFormAdvantages
                        isOpen={isAdvantagesOpen}
                        toggleStateOpen={toggleStateOpen}
                        advantages={selectedCabinObj?.cabinType.advantages}
                        amenities={selectedCabinObj?.cabinType.amenities}
                    />
                </div>
            </div>
            {showTariffDialog &&
                createPortal(
                    <CabinTariffDialog
                        onClose={() => toggleTariffDialog(false)}
                        data={tariffsDescription}
                    />,
                    document.body,
                )}
        </div>
    );
};

export default RequestFormCabinType;
